import React, { useState, useRef } from 'react';
import './MessageInput.css';

// Supported file types for upload
const ACCEPTED_FILE_TYPES = 'image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx';
const ACCEPTED_MIME_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
  'application/pdf',
  'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB max file size

const MessageInput = ({ onSendMessage, onSendFile, disabled, showFileAttachment = false }) => {
  const [message, setMessage] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0); // Track nested drag events

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmedMessage = message.trim();
    
    if (trimmedMessage && !disabled) {
      onSendMessage(trimmedMessage);
      setMessage(''); // Clear input after sending
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  // Validate and process file
  const processFile = (file) => {
    if (!file) return;

    // Validate file type
    const isValidType = ACCEPTED_MIME_TYPES.some(type => {
      if (type.includes('*')) {
        return file.type.startsWith(type.replace('*', ''));
      }
      return file.type === type;
    }) || file.type.startsWith('image/'); // Accept all images

    if (!isValidType) {
      alert('نوع الملف غير مدعوم. الأنواع المدعومة: صور، PDF، Word، Excel، نص.');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      alert('حجم الملف كبير جداً. الحد الأقصى هو 10 ميجابايت.');
      return;
    }

    // Call the onSendFile handler
    if (onSendFile && typeof onSendFile === 'function') {
      onSendFile(file);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    processFile(file);
    // Clear the file input for next selection
    e.target.value = '';
  };

  // Drag and Drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!showFileAttachment || disabled) return;
    
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsDragOver(false);
    dragCounterRef.current = 0;
    
    if (!showFileAttachment || disabled) return;
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      // Process only the first file
      processFile(files[0]);
    }
  };

  return (
    <form 
      className={`message-input ${isDragOver ? 'drag-over' : ''}`} 
      onSubmit={handleSubmit}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drop zone overlay - visible when dragging files */}
      {isDragOver && showFileAttachment && (
        <div className="drop-zone-overlay">
          <div className="drop-zone-content">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span>أفلت الملف هنا</span>
          </div>
        </div>
      )}
      
      {/* File attachment button - only visible when human agent is present */}
      {showFileAttachment && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_FILE_TYPES}
            onChange={handleFileChange}
            className="file-input-hidden"
            aria-label="إرفاق ملف"
          />
          <button
            type="button"
            onClick={handleFileButtonClick}
            disabled={disabled}
            className="file-attach-button"
            aria-label="إرفاق صورة أو ملف"
            title="إرفاق صورة أو ملف (أو اسحب وأفلت)"
          >
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
          </button>
        </>
      )}
      
      <input
        ref={inputRef}
        type="text"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="ادخل استفسارك هنا ..."
        disabled={disabled}
        className="message-input-field"
      />
      <button 
        type="submit" 
        disabled={!message.trim() || disabled}
        className="message-send-button"
        aria-label="إرسال الرسالة"
      >
        <img 
          src="/assets/send-msg.svg" 
          alt="إرسال" 
          width="24" 
          height="24"
          className="send-icon"
        />
      </button>
    </form>
  );
};

export default MessageInput;
